import { compareTiles } from "@kikik/mahjong-core";
import type { Tile } from "@kikik/mahjong-core";

export const AUTO_SORT_STORAGE_KEY = "kikik.mahjong.auto-sort";
export const MANUAL_ORDER_STORAGE_KEY = "kikik.mahjong.rack-order";

/**
 * Canonical neutral rack order: Characters 1-9, Bamboo 1-9, Circles 1-9,
 * winds East/South/West/North, then dragons Red/Green/White. Equal physical
 * copies use tile id as a deterministic tie-breaker. This delegates to the
 * core's documented kind order without ever changing the core hand itself.
 */
export function canonicalRack(tiles: readonly Tile[]): Tile[] {
  return tiles.slice().sort(compareTiles);
}

/** Keep surviving ids in their chosen order and append genuinely new tiles. */
export function reconcileRackOrder(previous: readonly number[], tiles: readonly Tile[]): number[] {
  const available = new Set(tiles.map((tile) => tile.id));
  const kept = previous.filter((id, index) => available.has(id) && previous.indexOf(id) === index);
  const known = new Set(kept);
  for (const tile of tiles) {
    if (!known.has(tile.id)) {
      kept.push(tile.id);
      known.add(tile.id);
    }
  }
  return kept;
}

export function arrangeRack(tiles: readonly Tile[], order: readonly number[]): Tile[] {
  const byId = new Map(tiles.map((tile) => [tile.id, tile]));
  return reconcileRackOrder(order, tiles)
    .map((id) => byId.get(id))
    .filter((tile): tile is Tile => tile !== undefined);
}

export function moveRackTile(order: readonly number[], source: number, target: number): number[] {
  if (source === target || !order.includes(source) || !order.includes(target)) return [...order];
  const next = order.filter((id) => id !== source);
  const targetIndex = next.indexOf(target);
  next.splice(targetIndex, 0, source);
  return next;
}

export function readAutoSort(storage?: Storage): boolean {
  try {
    return (storage ?? globalThis.localStorage)?.getItem(AUTO_SORT_STORAGE_KEY) !== "off";
  } catch {
    return true;
  }
}

export function readManualOrder(storage?: Storage): number[] {
  try {
    const parsed = JSON.parse(
      (storage ?? globalThis.localStorage)?.getItem(MANUAL_ORDER_STORAGE_KEY) ?? "[]",
    ) as unknown;
    return Array.isArray(parsed) && parsed.every((id) => Number.isInteger(id))
      ? (parsed as number[])
      : [];
  } catch {
    return [];
  }
}

export function persistRackPreferences(autoSort: boolean, order: readonly number[]): void {
  try {
    globalThis.localStorage?.setItem(AUTO_SORT_STORAGE_KEY, autoSort ? "on" : "off");
    globalThis.localStorage?.setItem(MANUAL_ORDER_STORAGE_KEY, JSON.stringify(order));
  } catch {
    // Storage is a preference only; private mode must never block play.
  }
}
